import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi, UserListParams, CreateUserData, UpdateUserData } from '@/api/users';
import { useActionFeedback } from '@/hooks/useActionFeedback';

export const useUsersList = (params: UserListParams = {}) => {
  return useQuery({
    queryKey: ['users', params],
    queryFn: () => usersApi.getAll(params),
  });
};

export const useUser = (id: string) => {
  return useQuery({
    queryKey: ['users', id],
    queryFn: () => usersApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();
  const feedback = useActionFeedback();

  return useMutation({
    mutationFn: (data: CreateUserData) => usersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      feedback.success('Pengguna berhasil dibuat');
    },
    onError: (error) => {
      feedback.error('Gagal membuat pengguna', error);
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  const feedback = useActionFeedback();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserData }) =>
      usersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      feedback.success('Pengguna berhasil diperbarui');
    },
    onError: (error) => {
      feedback.error('Gagal memperbarui pengguna', error);
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  const feedback = useActionFeedback();

  return useMutation({
    mutationFn: (id: string) => usersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      feedback.success('Pengguna berhasil dihapus');
    },
    onError: (error) => {
      feedback.error('Gagal menghapus pengguna', error);
    },
  });
};

export const useToggleUserActive = () => {
  const queryClient = useQueryClient();
  const feedback = useActionFeedback();

  return useMutation({
    mutationFn: (id: string) => usersApi.toggleActive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      feedback.success('Status pengguna berhasil diubah');
    },
    onError: (error) => {
      feedback.error('Gagal mengubah status pengguna', error);
    },
  });
};

export const useRoles = () => {
  return useQuery({
    queryKey: ['roles'],
    queryFn: () => usersApi.getRoles(),
  });
};
